import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCSOApprovalListQuery,
  useCSOOrganizationsQuery,
  usePublicBodiesApprovalListQuery,
  usePublicBodiesQuery,
} from '@/core/api/queries';
import CSOProfileService from '@/shared/services/cso/CSOProfileService';
import {
  findOrganizationInLists,
  mergeOrganizationLists,
} from './organizationNotificationUtils';

const CSO_PENDING_TYPES = new Set([
  'CSO_CREATED_PENDING_APPROVAL',
  'ORGANIZATION_CREATED_PENDING_APPROVAL',
  'CSO_SUBMITTED_FOR_APPROVAL',
  'CSO_APPROVED',
  'ORGANIZATION_SUBMITTED_FOR_APPROVAL',
  'ORGANIZATION_APPROVED',
  'ORGANIZATION_REJECTED',
]);

const PUBLIC_BODY_PENDING_TYPES = new Set([
  'PUBLIC_BODY_CREATED_PENDING_APPROVAL',
  'PUBLIC_BODY_SUBMITTED_FOR_APPROVAL',
  'PUBLIC_BODY_APPROVED',
  'ORGANIZATION_REJECTED',
]);

const ADMIN_ORG_REGISTRY_TYPES = new Set([
  ...CSO_PENDING_TYPES,
  ...PUBLIC_BODY_PENDING_TYPES,
]);

/**
 * Da li obaveštenje zahteva učitavanje CSO/javnotela registra (admin pending tipovi).
 *
 * @param {object[]} notifications
 * @returns {boolean}
 */
export function notificationsNeedOrganizationRegistry(notifications = []) {
  return (notifications || []).some((n) =>
    ADMIN_ORG_REGISTRY_TYPES.has(notificationType(n)),
  );
}

function notificationType(n) {
  return n?.notification_type || n?.type || '';
}

function collectPendingIds(notifications, typeSet) {
  return [
    ...new Set(
      (notifications || [])
        .filter((n) => typeSet.has(notificationType(n)))
        .map((n) => n?.related_entity_id)
        .filter((id) => id != null && id !== '')
        .map(String)
    ),
  ];
}

/**
 * Učitava CSO + javnotela registar za admin pending obaveštenja i dohvata nedostajuća imena po id.
 *
 * @param {{ enabled?: boolean, notifications?: object[] }} [options]
 * @param {boolean} [options.enabled] - Da li je hook aktivan.
 * @param {object[]} [options.notifications] - Lista obaveštenja za resolve org imena.
 * @returns {{ organizations: object[], organizationNameById: Record<string, string>, isLoading: boolean }}
 */
export function useNotificationOrganizations({ enabled = false, notifications = [] } = {}) {
  const csoApprovalQuery = useCSOApprovalListQuery({ enabled });
  const csoVerifiedQuery = useCSOOrganizationsQuery({ enabled });
  const pbApprovalQuery = usePublicBodiesApprovalListQuery({ enabled });
  const pbVerifiedQuery = usePublicBodiesQuery({ enabled });

  const organizations = useMemo(
    () =>
      mergeOrganizationLists(
        csoApprovalQuery.data,
        csoVerifiedQuery.data,
        pbApprovalQuery.data,
        pbVerifiedQuery.data
      ),
    [
      csoApprovalQuery.data,
      csoVerifiedQuery.data,
      pbApprovalQuery.data,
      pbVerifiedQuery.data,
    ]
  );

  const [fetchedNamesById, setFetchedNamesById] = useState({});
  const fetchStateRef = useRef({});

  useEffect(() => {
    if (!enabled) return undefined;

    const csoPendingIds = collectPendingIds(notifications, CSO_PENDING_TYPES);
    const pbPendingIds = collectPendingIds(notifications, PUBLIC_BODY_PENDING_TYPES);

    const missingCsoIds = csoPendingIds.filter((id) => {
      if (findOrganizationInLists(id, organizations)) return false;
      const state = fetchStateRef.current[`cso:${id}`];
      return state !== 'pending' && state !== 'done';
    });

    const missingPbIds = pbPendingIds.filter((id) => {
      if (findOrganizationInLists(id, organizations)) return false;
      const state = fetchStateRef.current[`pb:${id}`];
      return state !== 'pending' && state !== 'done';
    });

    if (!missingCsoIds.length && !missingPbIds.length) return undefined;

    let cancelled = false;
    missingCsoIds.forEach((id) => {
      fetchStateRef.current[`cso:${id}`] = 'pending';
    });
    missingPbIds.forEach((id) => {
      fetchStateRef.current[`pb:${id}`] = 'pending';
    });

    (async () => {
      const updates = {};

      await Promise.all([
        ...missingCsoIds.map(async (id) => {
          try {
            const data = await CSOProfileService.getOrganization(id);
            const name = (data?.name ?? data?.naziv ?? '').trim();
            if (name) updates[id] = name;
          } catch {
            /* CSO details may be unavailable */
          } finally {
            fetchStateRef.current[`cso:${id}`] = 'done';
          }
        }),
        ...missingPbIds.map(async (id) => {
          fetchStateRef.current[`pb:${id}`] = 'done';
        }),
      ]);

      if (!cancelled && Object.keys(updates).length > 0) {
        setFetchedNamesById((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, notifications, organizations]);

  const organizationNameById = useMemo(() => {
    const map = { ...fetchedNamesById };
    organizations.forEach((org) => {
      const id = org?.id ?? org?.organization_id;
      if (id == null || id === '') return;
      const name = (org?.name ?? org?.naziv ?? org?.organization_name ?? '').trim();
      if (name) map[String(id)] = name;
    });
    return map;
  }, [organizations, fetchedNamesById]);

  const isLoading =
    enabled &&
    (csoApprovalQuery.isLoading ||
      csoVerifiedQuery.isLoading ||
      pbApprovalQuery.isLoading ||
      pbVerifiedQuery.isLoading);

  return {
    organizations,
    organizationNameById,
    isLoading,
  };
}
